import { useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/app/auth";
import { useCan } from "@/app/access";
import { useProduct } from "@/api/hooks";
import { usePageCrumbs } from "@/components/shell/breadcrumb";
import { ProductFormPanel } from "@/components/products/ProductFormPanel";
import { productListSearch } from "@/components/products/productForm";
import { PageBody, PageHeader } from "@/components/ui/PageHeader";
import { ErrorState, LoadingState } from "@/components/ui/States";

export function ProductEditorPage() {
  const { id } = useParams();
  const { search } = useLocation();
  const sourceId = id ? undefined : new URLSearchParams(search).get("fromProduct") || undefined;
  return <ProductEditorLoader key={id ?? `new:${sourceId ?? ""}`} id={id} sourceId={sourceId} />;
}

function ProductEditorLoader({ id, sourceId }: { id?: string; sourceId?: string }) {
  const { t } = useTranslation();
  const [addingNext, setAddingNext] = useState(false);
  const { user } = useAuth();
  const can = useCan();
  const navigate = useNavigate();
  const { search, state } = useLocation();
  const listSearch = productListSearch(search);
  const listTo = `/products${listSearch}`;
  const detailTo = id ? `/products/${id}${listSearch}` : listTo;
  const canWrite = !!user && can(id ? "records:update" : "records:create");
  const { data, isLoading, refetch } = useProduct(canWrite ? id ?? sourceId : undefined);
  const returnToList = typeof state === "object" && state !== null && "returnToList" in state && state.returnToList === true;
  usePageCrumbs(id && data
    ? [{ label: data.productCode, to: detailTo }, { label: t("common.edit") }]
    : [{ label: t(sourceId && !addingNext ? "productEditor.duplicateTitle" : id ? "common.edit" : "products.newProduct") }], listTo);

  if (!canWrite) return <PageBody><ErrorState message={t("productEditor.noPermission")} /></PageBody>;
  if ((id || sourceId) && isLoading) return <PageBody><LoadingState label={t("common.loading")} /></PageBody>;
  if ((id || sourceId) && !data) return <PageBody><ErrorState message={t("products.errorLoad")} onRetry={() => void refetch()} /></PageBody>;

  return (
    <PageBody className="space-y-4">
      <PageHeader
        title={t(id ? "productEditor.editTitle" : sourceId && !addingNext ? "productEditor.duplicateTitle" : "products.newProduct")}
        subtitle={id && data ? `${data.productCode}${data.subRef ? ` · ${data.subRef}` : ""}` : t("productEditor.createHint")}
      />
      <ProductFormPanel
        key={id ?? `new:${sourceId ?? ""}`}
        variant="page"
        product={id ? data : undefined}
        duplicateFrom={sourceId ? data : undefined}
        onClose={() => navigate(returnToList ? listTo : detailTo)}
        onAddNext={() => setAddingNext(true)}
        onSaved={(savedId) => navigate(`/products/${savedId}${listSearch}`, { replace: true, state: { productSaved: true } })}
      />
    </PageBody>
  );
}
